import type { ChangeEventHandler, ChangeEvent, MouseEventHandler } from "react";
import type firebaseCompat from "firebase/compat/app";
import type { ProfileUser } from "../../types/profile";
import type { InterfacePreferences } from "../../utility/interfacePreferences";
import type { useProfileAccess } from "../../hooks/useProfileAccess";
import { errorCode, errorMessage } from "../../types/data";
import { useState, useRef, useEffect, useCallback, KeyboardEvent } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAppSelector as useSelector, useAppDispatch as useDispatch } from "../../store/hooks";
import { IoMdClose, IoMdLogOut, IoIosSettings, IoMdMenu } from "react-icons/io";
import { useFirebase } from "../../context/FirebaseContext";
import SearchMovies from "../SearchMovies";
import { useUserData } from "../../hooks/useUserData";
import { useUserBackgrounds } from "../../context/UserBackgroundContext";
import { getCachedOrFetch, invalidateCachedValue } from "../../utility/localDataCache";
import { FaImage } from "react-icons/fa";
import {
  IoCloudUploadOutline,
  IoColorPaletteOutline,
  IoImageOutline,
  IoLanguageOutline,
  IoLockClosedOutline,
  IoOptionsOutline
} from "react-icons/io5";
import defaultPhoto from "../../images/default-photo.jpeg";
import {
  getUserProfileCacheKey,
  USER_PROFILE_CACHE_OPTIONS
} from "../../utility/profileCache";
import { useI18n } from "../../i18n/useI18n";
import { useLanguagePreference } from "../../i18n/useLanguagePreference";
import {
  beginDemoLogout,
  clearDemoLogout,
  isDemoUserId,
  markDemoActivity,
  readDemoSession,
  saveDemoSession
} from "../../utility/demoSession";
import { useInterfacePreferences } from "../../hooks/useInterfacePreferences";
import { normalizeInterfacePreferences } from "../../utility/interfacePreferences";
import {
  PROFILE_PRIVACY_MODES,
  PUBLIC_PROFILES_COLLECTION,
  buildPublicProfileDocument,
  clearPendingProfilePrivacyMode,
  getPendingProfilePrivacyMode,
  getProfilePrivacyMode,
  setPendingProfilePrivacyMode
} from "../../utility/profilePrivacy";
import { syncProfileDirectoryEntry } from "../../utility/profileDirectoryApi";
import { signOutWithBarrier } from "../../utility/authTransition";
import { touchDemoSession } from "../../utility/demoApi";
import MyConfirmModal from "../customComponents/confirmModalDialog";

type ProfileAccess = Pick<ReturnType<typeof useProfileAccess>, "privacyMode" | "publicProfile">;
interface SettingsPanelProps {
  colors: string[][];
  checked: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  language?: string;
  onLanguageChange: ChangeEventHandler<HTMLInputElement>;
  privacyMode?: string;
  onPrivacyModeChange?: ChangeEventHandler<HTMLInputElement>;
  privacySaving?: boolean;
  privacyError?: string;
  interfacePreferences: InterfacePreferences;
  onInterfacePreferenceChange: (key: keyof InterfacePreferences, value: boolean) => void;
  onBackgroundUpload: ChangeEventHandler<HTMLInputElement>;
  backgroundUploading?: boolean;
  canUploadBackground?: boolean;
  canImport?: boolean;
  canChangePrivacy?: boolean;
  importPath: string;
  onClose?: () => void;
  t: (value: string) => string;
}

const isRulesMigrationPermissionError = (error: unknown) => {
  const code = errorCode(error).toLowerCase();
  const message = errorMessage(error).toLowerCase();
  return (code.includes("permission-denied") || message.includes("insufficient permissions"))
    && !message.includes("maximum of 1000 expressions");
};

export const persistProfilePrivacy = async ({
  userDocument,
  publicProfileDocument,
  privacyUpdate,
  publicProfile
}: { userDocument: Pick<firebaseCompat.firestore.DocumentReference, "update" | "set">; publicProfileDocument: Pick<firebaseCompat.firestore.DocumentReference, "set">; privacyUpdate: { publicProfileEnabled: boolean; privacyMode: string; privacyUpdatedAt: firebaseCompat.firestore.FieldValue }; publicProfile: ReturnType<typeof buildPublicProfileDocument> }) => {
  try {
    await userDocument.update(privacyUpdate);
  } catch (error) {
    if (!isRulesMigrationPermissionError(error)) throw error;
    // The currently deployed legacy rules know only this boolean. It still
    // enforces a safe public/closed boundary while the richer rules are being
    // rolled out, and prevents an atomic publicProfiles failure from rolling
    // the setting back entirely.
    await userDocument.set({
      publicProfileEnabled: privacyUpdate.publicProfileEnabled
    }, { merge: true });
    return { canonical: false, publicProfileSynced: false, migrationError: error };
  }

  try {
    await publicProfileDocument.set(publicProfile, { merge: true });
    return { canonical: true, publicProfileSynced: true };
  } catch (error) {
    return { canonical: true, publicProfileSynced: false, publicProfileError: error };
  }
};

const INTERFACE_PREFERENCE_OPTIONS: { key: keyof InterfacePreferences; title: string; description: string }[] = [
  {
    key: "celebrationModals",
    title: "Поздравительные окна",
    description: "Достижения, испытания и важные повышения уровня."
  },
  {
    key: "toastNotifications",
    title: "Всплывающие сообщения",
    description: "Короткие сообщения в правом верхнем углу."
  },
  {
    key: "friendActivityNotifications",
    title: "Активность друзей",
    description: "Новые оценки, достижения и другие события друзей."
  },
  {
    key: "legendaryHighlights",
    title: "Легендарное свечение",
    description: "Золотая рамка «Путешествий», карт недели и экспедиций."
  },
  {
    key: "notificationBadges",
    title: "Счётчик новых событий",
    description: "Цифра непрочитанных событий рядом с «Дневником»."
  },
  {
    key: "motionEffects",
    title: "Анимации и эффекты",
    description: "Декоративные переходы, сияние и эффекты открытия."
  }
];

const SETTINGS_TABS = [
  {
    id: "colors",
    title: "Цветовая схема",
    icon: IoColorPaletteOutline
  },
  {
    id: "language",
    title: "Язык интерфейса",
    icon: IoLanguageOutline
  },
  {
    id: "privacy",
    title: "Приватность профиля",
    icon: IoLockClosedOutline,
    requiresPrivacy: true
  },
  {
    id: "interface",
    title: "Интерфейс и уведомления",
    icon: IoOptionsOutline
  },
  {
    id: "background",
    title: "Фон приложения",
    icon: IoImageOutline,
    requiresBackgroundUpload: true
  },
  {
    id: "import",
    title: "Импорт оценок и списков",
    icon: IoCloudUploadOutline,
    requiresImport: true
  }
];

export const SettingsPanel = ({
  colors,
  checked,
  onChange,
  language,
  onLanguageChange,
  privacyMode,
  onPrivacyModeChange,
  privacySaving,
  privacyError,
  interfacePreferences,
  onInterfacePreferenceChange,
  onBackgroundUpload,
  backgroundUploading,
  canUploadBackground,
  canImport,
  canChangePrivacy,
  importPath,
  onClose,
  t
}: SettingsPanelProps) => {
  const availableTabs = SETTINGS_TABS.filter((tab) => {
    if (tab.requiresBackgroundUpload) return canUploadBackground;
    if (tab.requiresImport) return canImport;
    if (tab.requiresPrivacy) return canChangePrivacy;
    return true;
  });
  const [activeTab, setActiveTab] = useState(availableTabs[0]?.id || "colors");
  const selectedTab = availableTabs.some((tab) => tab.id === activeTab)
    ? activeTab
    : availableTabs[0]?.id;
  const selectedTabInfo = availableTabs.find((tab) => tab.id === selectedTab);
  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
    let nextIndex: number | null = null;
    if (event.key === "ArrowDown" || event.key === "ArrowRight") {
      nextIndex = (currentIndex + 1) % availableTabs.length;
    } else if (event.key === "ArrowUp" || event.key === "ArrowLeft") {
      nextIndex = (currentIndex - 1 + availableTabs.length) % availableTabs.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = availableTabs.length - 1;
    }

    if (nextIndex == null) return;
    event.preventDefault();
    setActiveTab(availableTabs[nextIndex].id);
    event.currentTarget.parentElement
      ?.querySelectorAll<HTMLButtonElement>('[role="tab"]')
      ?.[nextIndex]
      ?.focus();
  };

  return (
    <div
      className="color-toggle__panel settings-panel"
      role="dialog"
      aria-label={t("Настройки")}
    >
      <aside className="settings-panel__sidebar">
        <h4 className="settings-panel__sidebar-title">{t("Настройки")}</h4>
        <div
          className="settings-panel__tabs"
          role="tablist"
          aria-label={t("Настройки")}
        >
          {availableTabs.map((tab, index) => {
            const TabIcon = tab.icon;
            const isActive = selectedTab === tab.id;
            return (
              <button
                key={tab.id}
                id={`settings-tab-${tab.id}`}
                className={`settings-panel__tab${isActive ? " settings-panel__tab--active" : ""}`}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={`settings-content-${tab.id}`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => setActiveTab(tab.id)}
                onKeyDown={(event) => handleTabKeyDown(event, index)}
              >
                <TabIcon aria-hidden="true" />
                <span>{t(tab.title)}</span>
              </button>
            );
          })}
        </div>
      </aside>

      <div className="settings-panel__main">
        <header className="settings-panel__header">
          <h4>{t(selectedTabInfo?.title || "Настройки")}</h4>
          <button
            className="settings-panel__close"
            type="button"
            onClick={onClose}
            aria-label={t("Закрыть меню")}
          >
            <IoMdClose aria-hidden="true" />
          </button>
        </header>

        <section
          id={`settings-content-${selectedTab}`}
          className="settings-panel__content"
          role="tabpanel"
          aria-labelledby={`settings-tab-${selectedTab}`}
        >
          {selectedTab === "colors" && (
            <div className="settings-panel__section">
              <div className="settings-panel__colors">
                {colors.map((color, index) => (
                  <div className="form-check form-check-inline ms-2" key={color[0]}>
                    <input
                      className={`${color[0]}-radio form-check-input`}
                      type="radio"
                      name="color-radio"
                      id={`customRadio${index}`}
                      value={color[0]}
                      checked={checked === color[0]}
                      onChange={onChange}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`customRadio${index}`}
                      style={{ color: color[1] }}
                    >
                      {t(color[2])}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {selectedTab === "language" && (
            <div className="settings-panel__section">
              <div
                className="settings-panel__language"
                role="radiogroup"
                aria-label={t("Язык интерфейса")}
              >
                <label className="settings-panel__language-option">
                  <input
                    type="radio"
                    name="language-radio"
                    value="ru"
                    checked={language === "ru"}
                    onChange={onLanguageChange}
                  />
                  <span>{t("Русский")}</span>
                </label>
                <label className="settings-panel__language-option">
                  <input
                    type="radio"
                    name="language-radio"
                    value="en"
                    checked={language === "en"}
                    onChange={onLanguageChange}
                  />
                  <span>English</span>
                </label>
              </div>
            </div>
          )}

          {selectedTab === "privacy" && canChangePrivacy && (
            <div className="settings-panel__section">
              <p className="settings-panel__hint">
                {t("Кто может видеть мой профиль")}
              </p>
              <div
                className="settings-panel__language settings-panel__privacy"
                role="radiogroup"
                aria-label={t("Кто может видеть мой профиль")}
              >
                {[
                  [PROFILE_PRIVACY_MODES.public, "Все пользователи"],
                  [PROFILE_PRIVACY_MODES.friends, "Только друзья"],
                  [PROFILE_PRIVACY_MODES.private, "Никто"]
                ].map(([value, label]) => (
                  <label className="settings-panel__language-option" key={value}>
                    <input
                      type="radio"
                      name="privacy-radio"
                      value={value}
                      checked={privacyMode === value}
                      onChange={onPrivacyModeChange}
                      disabled={privacySaving}
                    />
                    <span>{t(label)}</span>
                  </label>
                ))}
              </div>
              <p className="settings-panel__notice">
                {t("Изменение применяется сразу ко всем ссылкам и разделам профиля.")}
              </p>
              {privacyError && (
                <p className="settings-panel__error" role="alert">{t(privacyError)}</p>
              )}
            </div>
          )}

          {selectedTab === "interface" && (
            <div className="settings-panel__section">
              <p className="settings-panel__hint">
                {t("Выберите, какие необязательные элементы показывать.")}
              </p>
              <div className="settings-panel__preferences">
                {INTERFACE_PREFERENCE_OPTIONS.map((option) => (
                  <label className="settings-panel__preference" key={option.key}>
                    <span className="settings-panel__preference-copy">
                      <strong>{t(option.title)}</strong>
                      <small>{t(option.description)}</small>
                    </span>
                    <input
                      type="checkbox"
                      checked={interfacePreferences[option.key]}
                      onChange={(event) => {
                        onInterfacePreferenceChange(option.key, event.currentTarget.checked);
                      }}
                      aria-label={t(option.title)}
                    />
                    <span className="settings-panel__switch" aria-hidden="true" />
                  </label>
                ))}
              </div>
              <p className="settings-panel__notice">
                {t("Окна, требующие решения, например запросы в друзья и подтверждения, остаются включены.")}
              </p>
            </div>
          )}

          {selectedTab === "background" && canUploadBackground && (
            <div className="settings-panel__section settings-panel__section--action">
              <label className={`settings-background-upload${backgroundUploading ? " settings-background-upload--loading" : ""}`}>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                  onChange={(event) => {
                    onClose?.();
                    onBackgroundUpload(event);
                  }}
                  disabled={backgroundUploading}
                />
                <FaImage />
                <span>{backgroundUploading ? t("Обработка…") : t("Загрузить изображение")}</span>
              </label>
            </div>
          )}

          {selectedTab === "import" && canImport && (
            <div className="settings-panel__section settings-panel__section--action">
              <Link className="settings-panel__link" to={importPath} onClick={onClose}>
                {t("Импорт оценок и списков")}
              </Link>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

const LogoutButton = ({ onLogout, title }: { onLogout?: MouseEventHandler<HTMLButtonElement>; title?: string }) => (
  <button
    className="logout-button"
    onClick={onLogout}
    title={title}
    aria-label={title}
  >
    <IoMdLogOut size={20} />
  </button>
);

function TopPanel({
  userId: propUserId,
  profileAccess = null,
  isMobileMenuOpen = false,
  onToggleMobileMenu,
  onCloseMobileMenu
}: { userId?: string | null; profileAccess?: ProfileAccess | null; isMobileMenuOpen?: boolean; onToggleMobileMenu?: () => void; onCloseMobileMenu?: () => void }) {
  const { auth, firebase, firestore } = useFirebase();
  const { userId: paramUserId } = useParams(); // Получаем userId из URL
  const userId = propUserId || paramUserId;
  const { user: currentUser } = useUserData();
  const authenticatedUserId = auth.currentUser?.uid || currentUser?.uid;
  const isDemoMode = isDemoUserId(authenticatedUserId);
  const themeColor = useSelector((state) => state.settings.themeColor);
  const interfacePreferences = useInterfacePreferences();
  const { t } = useI18n();
  const { language, setManualLanguage } = useLanguagePreference();
  const [colorPanelVisible, setColorPanelVisible] = useState(false);
  const [checkedColor, setCheckedColor] = useState(themeColor);
  const [backgroundUploading, setBackgroundUploading] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState<ProfileUser | null>(null);
  const [privacySaving, setPrivacySaving] = useState(false);
  const [privacyError, setPrivacyError] = useState("");
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const privacyDirectorySyncRef = useRef("");
  const { saveAppBackground } = useUserBackgrounds();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const colors = [
    ["green", "#81c784", "Изумрудный"],
    ["violet", "#c084fc", "Лавандовый"],
    ["blue", "#3faaff", "Морской"],
    ["brown", "#b86b42", "Карамельный"],
    ["pink", "#ff99c8", "Bubblegum"],
    ["red", "#eb1b1bff", "Гранатовый"]
  ];

  const requestLogout = () => {
    setColorPanelVisible(false);
    onCloseMobileMenu?.();
    setShowLogoutConfirmation(true);
  };

  const logout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    let preservedDemoSession = isDemoMode ? readDemoSession() : null;
    if (isDemoMode) beginDemoLogout();
    try {
      if (isDemoMode) {
        try {
          // The authenticated endpoint returns the server-owned session key.
          // Synchronize it before Firebase removes the ID token so an older or
          // partially overwritten browser pointer cannot strand the profile.
          await touchDemoSession(auth, {
            restoreMissing: true,
            timeoutMs: 5000
          });
          preservedDemoSession = readDemoSession() || preservedDemoSession;
        } catch (error) {
          // Logout must remain available while the optional activity endpoint
          // is offline. The last known pointer is preserved below.
          console.warn("Не удалось синхронизировать демо перед выходом:", error);
        }
      }
      await signOutWithBarrier(auth);
      if (
        preservedDemoSession &&
        preservedDemoSession.uid === authenticatedUserId
      ) {
        saveDemoSession(preservedDemoSession);
      }
      setShowLogoutConfirmation(false);
      navigate("/login", {
        replace: true,
        state: isDemoMode ? { demoSignedOut: true } : undefined
      });
    } catch (error) {
      if (isDemoMode) clearDemoLogout();
      throw error;
    } finally {
      setLoggingOut(false);
    }
  };

  const toggleColorPanel = () => {
    if (isMobileMenuOpen) {
      onCloseMobileMenu?.();
    }
    setColorPanelVisible((prev) => !prev);
  };

  const toggleRadioValue = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const nextColor = e.currentTarget.value;
      setCheckedColor(nextColor);
      dispatch({
        type: "setThemeColor",
        payload: nextColor
      });
      if (auth.currentUser?.uid) {
        const uid = auth.currentUser.uid;
        const cacheKey = getUserProfileCacheKey(auth.currentUser.uid);
        firestore.collection("users").doc(uid).set(
          { themeColor: nextColor },
          { merge: true }
        ).then(() => {
          invalidateCachedValue(cacheKey);
          markDemoActivity(uid);
        }).catch((error: unknown) => console.error("Не удалось сохранить цветовую схему:", error));
      }
    },
    [auth, dispatch, firestore]
  );

  const toggleLanguageValue = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setManualLanguage(event.currentTarget.value);
      markDemoActivity(auth.currentUser?.uid);
    },
    [auth, setManualLanguage]
  );

  const toggleInterfacePreference = useCallback(
    (key: keyof InterfacePreferences, value: boolean) => {
      const nextPreferences = normalizeInterfacePreferences({
        ...interfacePreferences,
        [key]: value
      });
      dispatch({
        type: "setInterfacePreferences",
        payload: nextPreferences
      });

      const uid = auth.currentUser?.uid;
      if (!uid) return;
      if (isDemoMode) {
        markDemoActivity(uid);
        return;
      }
      const cacheKey = getUserProfileCacheKey(uid);
      firestore.collection("users").doc(uid).set(
        { interfacePreferences: nextPreferences },
        { merge: true }
      ).then(() => {
        invalidateCachedValue(cacheKey);
      }).catch((error: unknown) => {
        console.error("Не удалось сохранить настройки интерфейса:", error);
      });
    },
    [auth, dispatch, firestore, interfacePreferences, isDemoMode]
  );

  const togglePrivacyMode = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const uid = auth.currentUser?.uid;
    const nextPrivacyMode = Object.values(PROFILE_PRIVACY_MODES).find(mode => mode === event.currentTarget.value);
    if (
      !uid ||
      isDemoMode ||
      privacySaving ||
      !nextPrivacyMode
    ) return;

    const previousProfile = currentUserProfile;
    setPrivacySaving(true);
    setPrivacyError("");
    let nextProfile: ProfileUser | null = null;
    try {
      const timestamp = firebase.firestore.FieldValue.serverTimestamp();
      nextProfile = {
        ...(currentUserProfile || {}),
        uid,
        name:
          currentUserProfile?.name ||
          currentUser?.displayName ||
          currentUser?.email?.split?.("@")?.[0] ||
          "Пользователь",
        photoURL: currentUserProfile?.photoURL || currentUser?.photoURL || "",
        privacyMode: nextPrivacyMode
      };
      setCurrentUserProfile(nextProfile);
      const privacyUpdate = {
        privacyMode: nextPrivacyMode,
        publicProfileEnabled: nextPrivacyMode === PROFILE_PRIVACY_MODES.public,
        privacyUpdatedAt: timestamp
      };
      const result = await persistProfilePrivacy({
        userDocument: firestore.collection("users").doc(uid),
        publicProfileDocument: firestore.collection(PUBLIC_PROFILES_COLLECTION).doc(uid),
        privacyUpdate,
        publicProfile: buildPublicProfileDocument({
          uid,
          profile: nextProfile,
          authUser: currentUser,
          updatedAt: timestamp
        })
      });
      let directorySynced = result.publicProfileSynced;
      if (!directorySynced) {
        try {
          const directoryProfile = await syncProfileDirectoryEntry({
            auth,
            privacyMode: nextPrivacyMode
          });
          directorySynced = Boolean(directoryProfile);
        } catch (directoryError) {
          console.warn("Не удалось синхронизировать безопасную карточку поиска:", directoryError);
        }
      }
      if (directorySynced) {
        clearPendingProfilePrivacyMode(uid);
      } else {
        setPendingProfilePrivacyMode(uid, nextPrivacyMode);
        console.warn("Приватность сохранена в режиме совместимости до обновления Firestore Rules.", result.migrationError);
      }
      if (result.publicProfileError && !directorySynced) {
        console.warn("Приватность сохранена, но поисковый профиль пока не синхронизирован:", result.publicProfileError);
      }
      await invalidateCachedValue(getUserProfileCacheKey(uid));
    } catch (error) {
      setCurrentUserProfile(previousProfile);
      setPrivacyError("Не удалось сохранить настройку приватности. Попробуйте ещё раз.");
      console.error("Не удалось сохранить приватность профиля:", error);
    } finally {
      setPrivacySaving(false);
    }
  }, [auth, currentUser, currentUserProfile, firebase, firestore, isDemoMode, privacySaving]);

  useEffect(() => setCheckedColor(themeColor), [themeColor]);

  // Finish a privacy change made while only the legacy rules were available.
  // This also repairs the searchable closed-profile card after the client or
  // the directory backend is updated, without asking the owner to toggle the
  // setting a second time.
  useEffect(() => {
    const uid = currentUser?.uid;
    const pendingMode = getPendingProfilePrivacyMode(uid);
    const legacyPrivateMode = (
      currentUserProfile &&
      !currentUserProfile.privacyMode &&
      currentUserProfile.publicProfileEnabled === false
    ) ? PROFILE_PRIVACY_MODES.private : null;
    const modeToSync = pendingMode || legacyPrivateMode;
    if (!uid || !modeToSync || isDemoMode) return;
    const syncKey = `${uid}:${modeToSync}`;
    if (privacyDirectorySyncRef.current === syncKey) return;
    privacyDirectorySyncRef.current = syncKey;
    void syncProfileDirectoryEntry({ auth, privacyMode: modeToSync })
      .then((profile) => {
        if (!profile) return;
        clearPendingProfilePrivacyMode(uid);
        setCurrentUserProfile((current) => ({
          ...(current || {}),
          ...profile,
          privacyMode: modeToSync
        }));
        invalidateCachedValue(getUserProfileCacheKey(uid));
      })
      .catch(() => {
        // The legacy publicProfileEnabled=false boundary remains enforced.
        // Retry on the next mount instead of weakening privacy.
      });
  }, [auth, currentUser?.uid, currentUserProfile, isDemoMode]);

  // ProfileAccessBoundary already owns the realtime public metadata listener.
  // Reuse it here so another tab changing privacy updates an open settings
  // panel without adding a duplicate Firestore subscription.
  useEffect(() => {
    if (!currentUser?.uid || currentUser.uid !== userId) return;
    if (!profileAccess?.publicProfile) return;
    const pendingMode = getPendingProfilePrivacyMode(currentUser.uid);
    setCurrentUserProfile((current) => ({
      ...(current || {}),
      ...profileAccess.publicProfile,
      privacyMode: pendingMode || profileAccess.privacyMode
    }));
  }, [currentUser?.uid, profileAccess?.privacyMode, profileAccess?.publicProfile, userId]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setCurrentUserProfile(null);
      return undefined;
    }

    let cancelled = false;
    const applyPendingPrivacy = (profile: ProfileUser | null) => {
      const pendingMode = getPendingProfilePrivacyMode(currentUser.uid);
      return profile && pendingMode ? { ...profile, privacyMode: pendingMode } : profile;
    };
    const fetchCurrentUserProfile = async () => {
      const snapshot = await firestore.collection("users").doc(currentUser.uid).get();
      return snapshot.exists
        ? applyPendingPrivacy({ uid: snapshot.id, ...snapshot.data() })
        : null;
    };

    const loadCurrentUserProfile = async () => {
      return getCachedOrFetch(
        getUserProfileCacheKey(currentUser.uid),
        fetchCurrentUserProfile,
        {
          ...USER_PROFILE_CACHE_OPTIONS,
          onBackgroundUpdate: (profile) => {
            if (!cancelled) setCurrentUserProfile(applyPendingPrivacy(profile) || null);
          }
        }
      );
    };

    loadCurrentUserProfile()
      .then((profile) => {
        if (!cancelled) setCurrentUserProfile(applyPendingPrivacy(profile) || null);
      })
      .catch((error: unknown) => {
        console.warn("Не удалось загрузить кеш аватара текущего пользователя:", error);
        if (!cancelled) setCurrentUserProfile(null);
      });

    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, firestore]);

  const handleAppBackgroundUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    if (isDemoMode) return;
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBackgroundUploading(true);
    try {
      await saveAppBackground(file);
    } catch (error) {
      console.error("Ошибка загрузки фона приложения:", error);
    } finally {
      setBackgroundUploading(false);
    }
  };

  const colorTogglePanel = useRef<HTMLDivElement | null>(null);
  const handleClickOutside = (event: MouseEvent) => {
    if (!(event.target instanceof Node)) return;
    if (
      colorTogglePanel.current &&
      !colorTogglePanel.current.contains(event.target)
    ) {
      setColorPanelVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  const isOwnProfile = currentUser?.uid && userId === currentUser.uid;
  const currentUserAvatar =
    currentUserProfile?.avatarUrl ||
    currentUserProfile?.photoURL ||
    currentUser?.photoURL ||
    defaultPhoto;

  return (
    <div className="top-panel">
      <div className={`top-panel__activity${!isOwnProfile ? " top-panel__activity--has-profile-return" : ""}`}>
        <button
          className={`burger-menu-button${isMobileMenuOpen ? " burger-menu-button--open" : ""}`}
          onClick={onToggleMobileMenu}
          aria-label={isMobileMenuOpen ? t("Закрыть меню") : t("Открыть меню")}
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? <IoMdClose size={26} /> : <IoMdMenu size={24} />}
        </button>
        <SearchMovies onUserInteraction={onCloseMobileMenu} />
        <div className="right-side">
          {!isOwnProfile && currentUser?.uid && (
            <div className="image-wrap samll-image">
              <Link to={`/${currentUser.uid}/profile`}>
                <img
                  src={currentUserAvatar}
                  alt={currentUserProfile?.name || (currentUser as typeof currentUser & { name?: string }).name || t("Профиль")}
                  className="user-photo"
                  loading="eager"
                  decoding="async"
                  onError={(event) => {
                    if (!event.currentTarget.dataset.fallbackApplied) {
                      event.currentTarget.dataset.fallbackApplied = "true";
                      event.currentTarget.src = defaultPhoto;
                    }
                  }}
                />
                <span> {t("Вернуться в мой профиль.")}</span>
              </Link>
            </div>
          )}
          {isOwnProfile && (
            <div className="color-settings" ref={colorTogglePanel}>
              <IoIosSettings
                size={20}
                onClick={toggleColorPanel}
                title={t("Настройки")}
              />
              {colorPanelVisible && (
                <SettingsPanel
                  colors={colors}
                  checked={checkedColor}
                  onChange={toggleRadioValue}
                  language={language}
                  onLanguageChange={toggleLanguageValue}
                  privacyMode={getProfilePrivacyMode(currentUserProfile || {})}
                  onPrivacyModeChange={togglePrivacyMode}
                  privacySaving={privacySaving}
                  privacyError={privacyError}
                  interfacePreferences={interfacePreferences}
                  onInterfacePreferenceChange={toggleInterfacePreference}
                  onBackgroundUpload={handleAppBackgroundUpload}
                  backgroundUploading={backgroundUploading}
                  canUploadBackground={!isDemoMode}
                  canImport={!isDemoMode}
                  canChangePrivacy={!isDemoMode}
                  importPath={`/${currentUser?.uid}/import`}
                  onClose={() => setColorPanelVisible(false)}
                  t={t}
                />
              )}
            </div>
          )}
          <LogoutButton onLogout={requestLogout} title={t("выход")} />
        </div>
      </div>
      {showLogoutConfirmation && (
        <MyConfirmModal
          title={t("Выйти из аккаунта?")}
          message={t("Вы действительно хотите выйти из аккаунта?")}
          confirmText={t(loggingOut ? "Выходим…" : "Выйти")}
          onCancel={() => {
            if (!loggingOut) setShowLogoutConfirmation(false);
          }}
          onConfirm={logout}
        />
      )}
    </div>
  );
}

export default TopPanel;
